<template>
  <div>
    <div class="tw-bg-white tw-shadow tw-border-t tw-border-gray-200">
      <base-content class="tw-pt-3 tw-pb-3.5">
        <base-breadcrumbs :breadcrumbs="breadcrumbs" />
      </base-content>
      <base-content class="tw-pt-0 tw-flex tw-items-center">
        <h1 class="tw-text-xl sm:tw-text-2xl tw-leading-tight tw-text-gray-900 tw-mb-0 tw-font-thin">Pharmacy Settings</h1>
      </base-content>
    </div>

    <base-content v-if="!isLoading" class="tw-flex tw-flex-col">
      <base-card class="tw-flex tw-flex-col">
        <div class="tw-text-lg tw-text-gray-700 tw-font-normal tw-mb-6">Select pharmacies available for direct orders</div>

        <div v-for="pharmacy in availablePharmacies" :key="pharmacy.value" class="tw-mb-4">
          <base-checkbox v-model="selectedPharmaciesLocal" :value="pharmacy.value">
            {{ pharmacy.label }}
          </base-checkbox>
        </div>

        <div class="tw-flex tw-justify-end tw-mt-6">
          <base-button :loading="isSaving" @click="savePharmacySettings" type="primary" :disabled="!isFormValid || isSaving">
            Save Changes
          </base-button>
        </div>
      </base-card>

      <!-- New Card for Bulk Medications Auto-Fill and Bulk Update -->
      <base-card class="tw-hidden tw-flex-col tw-mt-8">
        <div class="tw-text-lg tw-text-gray-700 tw-font-normal tw-mb-6">Bulk Medications Auto-Fill</div>

        <div class="tw-flex tw-justify-end tw-mt-4">
          <base-button :loading="isBulkUpdating" @click="bulkUpdateMedications" type="primary" :disabled="isBulkUpdating">
            Bulk Update
          </base-button>
        </div>
      </base-card>
    </base-content>

    <base-content v-else>
      <base-card class="tw-flex tw-flex-col">
        <base-skeleton-loader class="tw-h-6 tw-w-48 tw-mb-6" />
        <div class="tw-mb-4"><base-skeleton-loader class="tw-h-5 tw-w-32" /></div>
        <div class="tw-mb-4"><base-skeleton-loader class="tw-h-5 tw-w-36" /></div>
        <div class="tw-flex tw-justify-end tw-mt-6">
          <base-skeleton-loader class="tw-h-10 tw-w-24" />
        </div>
      </base-card>
    </base-content>
  </div>
</template>

<script>
import { mapActions } from "vuex";

// Pharmacy constants configuration
const PHARMACY_CONFIG = {
  LIFEFILE: {
    value: "Lifefile",
    label: "Innovation (LifeFile)",
  },
  EVERWELL: {
    value: "Everwell",
    label: "Everwell (SiCompounding)",
  },
  RPC2B: {
    value: "RPC2B",
    label: "RPC2B (SiCompounding)",
  },
};

export default {
  name: "BusinessPharmacyView",
  components: {},
  props: {
    businessId: String,
  },
  data() {
    return {
      isLoading: false,
      isSaving: false,
      selectedPharmaciesLocal: [],
      initialPharmacySetting: null,
      isBulkUpdating: false,
      availablePharmacies: [PHARMACY_CONFIG.LIFEFILE, PHARMACY_CONFIG.EVERWELL, PHARMACY_CONFIG.RPC2B],
    };
  },
  computed: {
    breadcrumbs() {
      return [
        {
          title: "Settings",
          onClick: () => this.$router.push(`/businesses/${this.businessId}/business`),
        },
        { title: "Pharmacy Settings" },
      ];
    },
    isFormValid() {
      return this.selectedPharmaciesLocal.length > 0;
    },
  },
  async mounted() {
    await this.getPharmacySettings();
  },
  methods: {
    ...mapActions("businessActivation", { businessActivationInit: "init" }),

    async getPharmacySettings() {
      this.isLoading = true;
      try {
        const res = await this.$axios.get(`/businesses/${this.businessId}/pharmacy-settings`);
        const pharmacyData = res.data.pharmacy;
        this.initialPharmacySetting = pharmacyData;
        this.parsePharmacySettings(pharmacyData);
      } catch (err) {
        console.error("Error fetching pharmacy settings:", err);
        this.EventBus.$emit("GLOBAL_NOTIFY", {
          title: "Error",
          message: "Failed to load pharmacy settings. Please try again.",
          type: "error",
        });
      } finally {
        this.isLoading = false;
      }
    },

    parsePharmacySettings(pharmacyObject) {
      if (!pharmacyObject || !pharmacyObject.options || pharmacyObject.options.length === 0) {
        this.selectedPharmaciesLocal = [];
        return;
      }

      // Get all valid pharmacy values from configuration
      const validPharmacyValues = Object.values(PHARMACY_CONFIG).map((p) => p.value);

      this.selectedPharmaciesLocal = pharmacyObject.options.filter((option) => validPharmacyValues.includes(option.name)).map((option) => option.name);
    },

    async savePharmacySettings() {
      if (!this.isFormValid) {
        this.EventBus.$emit("GLOBAL_NOTIFY", {
          title: "Validation Error",
          message: "Please select at least one pharmacy.",
          type: "warning",
        });
        return;
      }

      this.isSaving = true;
      try {
        // Build payload options from selected pharmacies
        const payloadOptions = this.selectedPharmaciesLocal.map((pharmacyValue) => ({
          name: pharmacyValue,
          value: pharmacyValue,
        }));

        const payload = {
          pharmacy: {
            options: payloadOptions,
          },
        };

        await this.$axios.put(`/businesses/${this.businessId}/pharmacy-settings`, payload);
        await this.getPharmacySettings();

        this.EventBus.$emit("GLOBAL_NOTIFY", {
          title: "Success",
          message: "Pharmacy settings updated successfully.",
        });
      } catch (err) {
        console.error("Error saving pharmacy settings:", err);
        this.EventBus.$emit("GLOBAL_NOTIFY", {
          title: "Error",
          message: "Failed to save pharmacy settings. Please try again.",
          type: "error",
        });
      } finally {
        this.isSaving = false;
      }
    },

    async bulkUpdateMedications() {
      this.isBulkUpdating = true;
      try {
        await this.$axios.put(`/businesses/${this.businessId}/bulk_update_medicationitem-categories`);
        this.EventBus.$emit("GLOBAL_NOTIFY", {
          title: "Success",
          message: "Bulk medication update completed successfully.",
        });
      } catch (err) {
        console.error("Error during bulk medication update:", err);
        this.EventBus.$emit("GLOBAL_NOTIFY", {
          title: "Error",
          message: "Bulk medication update failed. Please try again.",
          type: "error",
        });
      } finally {
        this.isBulkUpdating = false;
      }
    },
  },
};
</script>
